import React, { useMemo, useState } from 'react';
import { UsersIcon, BarChart3Icon, MousePointerClickIcon, HelpCircleIcon, AlertTriangleIcon, SmartphoneIcon, MonitorIcon, ArrowLeftIcon, RotateCcwIcon, InboxIcon } from 'lucide-react';
import { getAllSessions, clearSessions, computeAnalytics } from '../data/analyticsStore';
import { MetricCard } from '../components/dashboard/MetricCard';
import { FunnelChart } from '../components/dashboard/FunnelChart';
import { QuestionBreakdown } from '../components/dashboard/QuestionBreakdown';
import { ResultDistribution } from '../components/dashboard/ResultDistribution';
export function Dashboard() {
  const [refreshKey, setRefreshKey] = useState(0);
  const analytics = useMemo(() => {
    const sessions = getAllSessions();
    return computeAnalytics(sessions);
  }, [refreshKey]);
  const handleReset = () => {
    clearSessions();
    setRefreshKey(k => k + 1);
  };
  const handleRefresh = () => {
    setRefreshKey(k => k + 1);
  };
  return <div className="min-h-screen w-full" style={{
    background: 'linear-gradient(180deg, #0a0a0f 0%, #0c1222 50%, #0a0a0f 100%)'
  }} data-id="element-205">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-id="element-206">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-207">
          <div className="flex items-center gap-4" data-id="element-208">
            <a href="/" className="flex items-center gap-1.5 text-sm text-white/40 hover:text-cyan-400 transition-colors" aria-label="Back to Quiz" data-id="element-209">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-210" />
              Quiz
            </a>
            <div data-id="element-211">
              <h1 className="text-2xl sm:text-3xl font-bold text-white" data-id="element-212">
                Quiz Analytics
              </h1>
              <p className="text-sm text-white/30 mt-0.5" data-id="element-213">
                48H AI Cash Machine — Real Visitor Data
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-214">
            <button onClick={handleRefresh} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-white/[0.06] border border-white/10 text-white/50 hover:text-cyan-400 hover:border-cyan-500/30 transition-all" data-id="element-215">
              <RotateCcwIcon className="w-3.5 h-3.5" data-id="element-216" />
              Refresh
            </button>
            <button onClick={handleReset} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-white/[0.06] border border-white/10 text-white/50 hover:text-red-400 hover:border-red-400/30 transition-all" data-id="element-217">
              <RotateCcwIcon className="w-3.5 h-3.5" data-id="element-218" />
              Clear All Data
            </button>
          </div>
        </div>

        {analytics.totalSessions === 0 ? <div className="text-center py-20" data-id="element-219">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/[0.04] border border-white/10 mb-6" data-id="element-220">
              <InboxIcon className="w-8 h-8 text-white/20" data-id="element-221" />
            </div>
            <p className="text-white/50 text-lg mb-2" data-id="element-222">No visitor data yet</p>
            <p className="text-white/25 text-sm max-w-md mx-auto" data-id="element-223">
              Real sessions will appear here as unique visitors interact with
              the quiz. Each browser is tracked once — no duplicates.
            </p>
          </div> : <>
            {/* Metric Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-6" data-id="element-224">
              <MetricCard label="Visitors" value={analytics.totalSessions} icon={<UsersIcon className="w-4 h-4" data-id="element-226" />} data-id="element-225" />
              <MetricCard label="Completion" value={`${analytics.completionRate.toFixed(0)}%`} trend={analytics.completionRate > 40 ? 'up' : 'down'} trendValue={analytics.completionRate > 40 ? 'Good' : 'Low'} icon={<BarChart3Icon className="w-4 h-4" data-id="element-228" />} data-id="element-227" />
              <MetricCard label="CTA Clicks" value={`${analytics.ctaClickRate.toFixed(0)}%`} trend={analytics.ctaClickRate > 50 ? 'up' : analytics.ctaClickRate > 30 ? 'neutral' : 'down'} trendValue={analytics.ctaClickRate > 50 ? 'Great' : analytics.ctaClickRate > 30 ? 'OK' : 'Low'} subtitle="of completions" icon={<MousePointerClickIcon className="w-4 h-4" data-id="element-230" />} data-id="element-229" />
              <MetricCard label="Avg Questions" value={analytics.avgQuestionsAnswered.toFixed(1)} subtitle="of 12 total" icon={<HelpCircleIcon className="w-4 h-4" data-id="element-232" />} data-id="element-231" />
              <MetricCard label="Biggest Drop" value={analytics.biggestDropStep} trend="down" trendValue={`${analytics.biggestDropPercent.toFixed(0)}% lost`} icon={<AlertTriangleIcon className="w-4 h-4" data-id="element-234" />} data-id="element-233" />
              <MetricCard label="Mobile" value={`${(analytics.mobileCount / analytics.totalSessions * 100).toFixed(0)}%`} subtitle={`${analytics.mobileCount}m · ${analytics.desktopCount}d`} icon={<SmartphoneIcon className="w-4 h-4" data-id="element-236" />} data-id="element-235" />
            </div>

            {/* Funnel Chart */}
            <div className="mb-6" data-id="element-237">
              <FunnelChart data={analytics.funnel} data-id="element-238" />
            </div>

            {/* Bottom Row */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-id="element-239">
              <div className="lg:col-span-3" data-id="element-240">
                <QuestionBreakdown questionAnswers={analytics.questionAnswers} data-id="element-241" />
              </div>
              <div className="lg:col-span-2" data-id="element-242">
                <ResultDistribution data={analytics.resultDistribution} data-id="element-243" />
              </div>
            </div>
          </>}
      </div>
    </div>;
}